import React, { useEffect, useMemo, useState } from "react";
import "./ChatTemplateSender.css";
import { ListarMetaWhatsAppTemplates } from "../../../Services/MetaWhatsAppTemplate";
import { EnviarTemplateWhatsApp } from "../../../Services/WhatsApp";
import ChatTemplatePreview from "./ChatTemplatePreview";

export default function ChatTemplateSender({
    aberto,
    onFechar,
    contaMeta,
    numeroDestino,
    onEnviadoSucesso
}) {
    const [loading, setLoading] = useState(false);
    const [enviando, setEnviando] = useState(false);

    const [templates, setTemplates] = useState([]);
    const [templateIdSelecionado, setTemplateIdSelecionado] = useState("");
    const [valoresParametros, setValoresParametros] = useState({}); // { ordem: valor }

    const templateSelecionado = useMemo(() => {
        if (!templateIdSelecionado) return null;

        return (
            templates.find((t) => String(t.metaWhatsAppTemplateId) === String(templateIdSelecionado)) || null
        );
    }, [templates, templateIdSelecionado]);

    const parametrosOrdenados = useMemo(() => {
        const list = templateSelecionado?.parametros || [];
        return [...list].sort(
            (a, b) =>
                (a.metaWhatsAppTemplateParametroOrdem || 0) - (b.metaWhatsAppTemplateParametroOrdem || 0)
        );
    }, [templateSelecionado]);

    // Carrega templates ao abrir modal
    useEffect(() => {
        if (!aberto) return;

        const carregarTemplates = async () => {
            setLoading(true);

            const response = await ListarMetaWhatsAppTemplates();

            setLoading(false);

            if (!response.success) {
                alert(response.message || "Erro ao carregar templates.");
                return;
            }

            setTemplates(response.templates || []);
            setTemplateIdSelecionado("");
            setValoresParametros({});
        };

        carregarTemplates();
    }, [aberto]);

    const alterarValorParametro = (ordem, valor) => {
        setValoresParametros((prev) => ({
            ...prev,
            [ordem]: valor
        }));
    };

    const validarEnvio = () => {
        if (!contaMeta) return "Conta Meta não informada.";
        if (!numeroDestino) return "Número de destino não informado.";
        if (!templateSelecionado) return "Selecione um template.";

        for (const p of parametrosOrdenados) {
            const ordem = p.metaWhatsAppTemplateParametroOrdem;
            const valor = (valoresParametros[ordem] || "").trim();

            if (!valor) {
                return `Preencha o parâmetro ${ordem} (${p.metaWhatsAppTemplateParametroLabel}).`;
            }
        }

        return "";
    };

    const enviarTemplate = async () => {
        const erro = validarEnvio();
        if (erro) {
            alert(erro);
            return;
        }

        setEnviando(true);

        const parametros = parametrosOrdenados.map((p) => ({
            ordem: p.metaWhatsAppTemplateParametroOrdem,
            valor: (valoresParametros[p.metaWhatsAppTemplateParametroOrdem] || "").trim()
        }));

        const response = await EnviarTemplateWhatsApp(
            contaMeta,
            numeroDestino,
            templateSelecionado.metaWhatsAppTemplateCodigo,
            parametros,
            templateSelecionado.metaWhatsAppTemplateUrlImageHeader

        );

        setEnviando(false);

        if (!response.success) {
            alert(response.message || "Erro ao enviar template.");
            return;
        }

        if (onEnviadoSucesso) {
            onEnviadoSucesso(response.resultData);
        }

        onFechar();
    };

    if (!aberto) return null;

    return (
        <div className="chat-template-modal-overlay">
            <div className="chat-template-modal">
                {/* HEADER */}
                <div className="chat-template-modal-header">
                    <div className="chat-template-title">Enviar Template</div>

                    <button
                        type="button"
                        className="chat-template-close"
                        onClick={onFechar}
                        disabled={enviando}
                        aria-label="Fechar"
                        title="Fechar"
                    >
                        ✕
                    </button>
                </div>

                {/* BODY */}
                <div className="chat-template-modal-body">
                    {loading ? (
                        <div>Carregando templates...</div>
                    ) : (
                        <>
                            <label className="chat-template-label">Template</label>

                            <select
                                className="chat-template-select"
                                value={templateIdSelecionado}
                                onChange={(e) => {
                                    setTemplateIdSelecionado(e.target.value);
                                    setValoresParametros({});
                                }}
                                disabled={enviando}
                            >
                                <option value="">Selecione...</option>

                                {templates.map((t) => (
                                    <option key={t.metaWhatsAppTemplateId} value={t.metaWhatsAppTemplateId}>

                                        {t.metaWhatsAppTemplateDescricao}
                                    </option>
                                ))}
                            </select>

                            {/* PREVIEW (CENTRALIZADO E ISOLADO) */}
                            {templateSelecionado && (
                                <div className="chat-template-preview-container">
                                    <div className="chat-template-preview-title">
                                        Prévia do template
                                    </div>

                                    <div className="chat-template-preview-wrapper">
                                        <ChatTemplatePreview
                                            template={templateSelecionado}
                                            valoresParametros={valoresParametros}
                                            size="sm" // pode trocar para md ou lg
                                        />
                                    </div>
                                </div>
                            )}

                            {/* PARÂMETROS */}
                            {templateSelecionado && (
                                <div className="chat-template-params">
                                    {parametrosOrdenados.length === 0 ? (
                                        <div>Este template não possui parâmetros.</div>
                                    ) : (
                                        parametrosOrdenados.map((p) => {
                                            const ordem = p.metaWhatsAppTemplateParametroOrdem;

                                            return (
                                                <div
                                                    key={p.metaWhatsAppTemplateParametroId || ordem}
                                                    className="chat-template-param-row"
                                                >
                                                    <div className="chat-template-param-title">
                                                        {ordem}. {p.metaWhatsAppTemplateParametroLabel}
                                                    </div>

                                                    <input
                                                        type="text"
                                                        className="chat-template-param-input"
                                                        value={valoresParametros[ordem] || ""}
                                                        placeholder={p.metaWhatsAppTemplateParametroExemplo || ""}
                                                        onChange={(e) => alterarValorParametro(ordem, e.target.value)}
                                                        disabled={enviando}
                                                    />
                                                </div>
                                            );
                                        })
                                    )}
                                </div>
                            )}
                        </>
                    )}
                </div>

                {/* FOOTER */}
                <div className="chat-template-modal-footer">
                    {enviando ? (
                        "Enviando..."
                    ) : (<button
                        type="button"
                        className="chat-send-button"
                        onClick={enviarTemplate}
                        disabled={enviando || loading}
                        aria-label="Enviar template"
                        title="Enviar"
                    >

                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            height="24"
                            viewBox="0 0 24 24"
                            width="24"
                            fill="#fff"
                            aria-hidden="true"
                        >
                            <path d="M0 0h24v24H0z" fill="none" />
                            <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z" />
                        </svg>

                    </button>)}
                </div>
            </div>
        </div>
    );
}
